import { CheckCircleOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import ModuleCard from 'components/cards/ModuleCard';
import { Button } from 'components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { useUpdateLicenseCode } from 'hooks/useUpdateLicenseCode';
import posthog from 'posthog-js';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { toast } from 'sonner';
import { selectCurrentUser } from 'store/users/selectors';

type CodeFormValues = { licenseCode: string };
type CompanyFormValues = { companyName: string };

export default function PartnerSection() {
  const { t } = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const codeForm = useForm<CodeFormValues>({
    mode: 'onChange',
    defaultValues: { licenseCode: (currentUser?.license_code || '').toUpperCase() },
  });
  const companyForm = useForm<CompanyFormValues>({ defaultValues: { companyName: '' } });
  const [isAskingCompanyName, setIsAskingCompanyName] = useState(false);
  const [partnerRequestSubmitted, setPartnerRequestSubmitted] = useState(false);
  const [licenseSaved, setLicenseSaved] = useState(false);

  const { run: updateLicenseCode, loading: loadingLicenseCode } = useUpdateLicenseCode({
    onSuccess: () => {
      setLicenseSaved(true);
      codeForm.reset();
    },
    posthogSource: 'homepage',
  });

  const { run: createPartnerRequest, loading: loadingPartnerRequest } = useRequest(
    (formValues: CompanyFormValues) => ApiUsersManager.createPartnerRequest({ company_name: formValues.companyName }),
    {
      manual: true,
      onSuccess: () => {
        setPartnerRequestSubmitted(true);
        toast.success(t('settings.partner.requestCreated'));
        posthog.capture('settings.partner_request_created', { source: 'homepage' });
      },
      onError: () => {
        toast.error(t('settings.license.errors.default'));
      },
    },
  );

  // Hide the section for users who already have a license code on page load,
  // but keep showing it right after a successful activation so we can display
  // the confirmation message.
  const hasLicenseCode = !!currentUser?.license_code && !licenseSaved;

  if (hasLicenseCode || currentUser?.is_caregiver) {
    // User has a license code: do not show this section
    return null;
  }

  return (
    <div className="w-full">
      <ModuleCard title={t('home.partner.title')}>
        {licenseSaved && (
          <div className="mt-4 mb-2 flex flex-col items-center justify-center px-8 text-center">
            <CheckCircleOutlined className="text-foreground mb-4 text-4xl" />
            <span>{t('home.partner.successMessage')}</span>
          </div>
        )}

        {!licenseSaved && (
          <>
            <p className="text-muted-foreground m-0">
              {isAskingCompanyName ? t('home.partner.companyNameDescription') : t('home.partner.codeDescription')}
            </p>
            {!partnerRequestSubmitted && (
              <div className="relative min-h-28 overflow-hidden">
                <div
                  className="absolute top-0 left-0 w-full transition-[transform,opacity] duration-300 ease-in-out"
                  style={{
                    transform: isAskingCompanyName ? 'translateX(-100%)' : 'translateX(0)',
                    opacity: isAskingCompanyName ? 0 : 1,
                    pointerEvents: isAskingCompanyName ? 'none' : 'auto',
                  }}
                >
                  <Form {...codeForm}>
                    <form
                      className="mt-4 w-full"
                      onSubmit={codeForm.handleSubmit(values => {
                        updateLicenseCode(values);
                      })}
                    >
                      <div className="flex items-center gap-2">
                        <FormField
                          control={codeForm.control}
                          name="licenseCode"
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input type="text" placeholder={t('home.partner.codePlaceholder')} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button type="submit" loading={loadingLicenseCode}>
                          {t('common.confirm')}
                        </Button>
                      </div>
                      <div className="mt-6 flex flex-col gap-2">
                        <Button
                          type="button"
                          variant="link"
                          className="w-full"
                          onClick={() => {
                            codeForm.reset();
                            setIsAskingCompanyName(true);
                          }}
                        >
                          {t('home.partner.dontKnowCode')}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </div>

                <div
                  className="absolute top-0 left-0 w-full transition-[transform,opacity] duration-300 ease-in-out"
                  style={{
                    transform: isAskingCompanyName ? 'translateX(0)' : 'translateX(100%)',
                    opacity: isAskingCompanyName ? 1 : 0,
                    pointerEvents: isAskingCompanyName ? 'auto' : 'none',
                  }}
                >
                  <Form {...companyForm}>
                    <form
                      className="mt-4 w-full"
                      onSubmit={companyForm.handleSubmit(values => {
                        createPartnerRequest(values);
                      })}
                    >
                      <div className="flex items-center gap-2">
                        <FormField
                          control={companyForm.control}
                          name="companyName"
                          rules={{ required: t('home.partner.companyNameRequired') }}
                          render={({ field }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input placeholder={t('home.partner.companyNamePlaceholder')} {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button type="submit" loading={loadingPartnerRequest}>
                          {t('common.confirm')}
                        </Button>
                      </div>
                      <div className="mt-6 flex flex-col gap-2">
                        <Button
                          type="button"
                          variant="link"
                          className="w-full"
                          onClick={() => {
                            companyForm.reset();
                            setIsAskingCompanyName(false);
                          }}
                        >
                          {t('common.cancel')}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </div>
              </div>
            )}
          </>
        )}
        {partnerRequestSubmitted && !licenseSaved && (
          <p className="text-foreground mt-4 text-sm">{t('home.partner.requestSubmittedMessage')}</p>
        )}
      </ModuleCard>
    </div>
  );
}
